import type { PersistedEnvelope } from "../types";
import type { StorageBackend } from "./StorageBackend";

/** Process-wide map backing the test-only storage implementation. */
const memoryStore = new Map<string, PersistedEnvelope<unknown>>();

/**
 * In-memory backend for unit tests only.
 *
 * @remarks Must not appear on any slice in `ALL_PERSISTED_SLICES`; registry assertion enforces this.
 */
export const inMemoryTestBackend: StorageBackend = {
    "kind": "inMemoryTest",

    /**
     * Returns a stored envelope synchronously wrapped in a resolved Promise.
     *
     * @typeParam T Expected `data` type.
     * @param key Slice key.
     */
    get<T>( key: string ): Promise<PersistedEnvelope<T> | null> {
        const value = memoryStore.get( key );

        return Promise.resolve( ( value as PersistedEnvelope<T> | undefined ) ?? null );
    },

    /**
     * Stores an envelope in the in-memory map.
     *
     * @typeParam T Envelope `data` type.
     * @param key Slice key.
     * @param envelope Versioned blob.
     */
    set<T>( key: string, envelope: PersistedEnvelope<T> ): Promise<void> {
        memoryStore.set( key, envelope as PersistedEnvelope<unknown> );

        return Promise.resolve();
    },

    /**
     * Removes one key from the in-memory map.
     *
     * @param key Slice key.
     */
    del( key: string ): Promise<void> {
        memoryStore.delete( key );

        return Promise.resolve();
    },

    /**
     * Lists all keys currently held in the test store.
     */
    keys(): Promise<string[]> {
        return Promise.resolve( [ ...memoryStore.keys() ] );
    },
};

/**
 * Clears the in-memory store between tests.
 *
 * @remarks Test helper only; not used in production boot paths.
 */
export const clearInMemoryTestBackend = (): void => {
    memoryStore.clear();
};
